import { createRoot } from "react-dom/client";
import { PrimeReactProvider } from "primereact/api";
import { LDProvider } from "launchdarkly-react-client-sdk";

import {
  DDErrorBoundary,
  getDatadogVersion,
  initDatadog,
  initDatadogLogs,
} from "sj-ui-toolkit/src/integrations";
import { ThemeProvider } from "sj-ui-toolkit/src/theme/ThemeProvider";
import { ToastProvider } from "sj-ui-toolkit/src/hooks/ToastContext";

// Atlas base styles
import "sj-ui-toolkit/src/atlas/styles/index.scss";

// App-level Tailwind entry
import "./styles.css";

// Atlas theme utility classes
import "sj-ui-toolkit/src/atlas/utils/themeUtilities.css";

import App from "./App";

// ---------------------------------------------------------------------------
// Version / logging
// ---------------------------------------------------------------------------

const version = getDatadogVersion();
console.log(`🚀 admin${version ? `: v${version}` : " (no version)"}`);

// ---------------------------------------------------------------------------
// Datadog RUM
// ⚠️  These credentials are UNIQUE to the admin RUM application.
//     Set VITE_APP_DATADOG_APP_ID and VITE_APP_DATADOG_CLIENT_TOKEN as CI/CD
//     secrets once the RUM application is provisioned in Datadog.
//     Initialization is silently skipped when either variable is absent
//     (local dev, preview builds, or before provisioning).
// ---------------------------------------------------------------------------

const ddAppId = import.meta.env.VITE_APP_DATADOG_APP_ID as string | undefined;
const ddClientToken = import.meta.env.VITE_APP_DATADOG_CLIENT_TOKEN as string | undefined;

if (ddAppId && ddClientToken) {
  initDatadog({
    applicationId: ddAppId,
    clientToken: ddClientToken,
    service: "admin-frontend",
    version,
  });

  initDatadogLogs({
    clientToken: ddClientToken,
    service: "admin-frontend",
    version,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ["error"],
  });
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element #root not found");

const defaultLaunchDarklyClientSideId = "638e4dc08684e4120d9a92ef";
const launchDarklyClientSideId =
  import.meta.env.VITE_APP_LAUNCHDARKLY_ID?.trim() || defaultLaunchDarklyClientSideId;

createRoot(rootEl).render(
  <PrimeReactProvider>
    <ThemeProvider loaderOptions={{ loaderOn: true, hideLoaderText: false }}>
      <LDProvider clientSideID={launchDarklyClientSideId}>
        <ToastProvider>
          <DDErrorBoundary>
            <App />
          </DDErrorBoundary>
        </ToastProvider>
      </LDProvider>
    </ThemeProvider>
  </PrimeReactProvider>
);
